
import { getTodayString } from "@/utils/dateUtils";
import type { GameResult } from "@/lib/types/game";
import type { PuzzleData } from '@/lib/types/puzzle-types';

export function saveGameResult(result: GameResult) {
  localStorage.setItem("last_game_result", JSON.stringify(result));
}

export function getLastGameResult(): GameResult | null {
  const result = localStorage.getItem("last_game_result");
  return result ? JSON.parse(result) : null;
}

export function markGameCompleted(date: string) {
  localStorage.setItem("game_completed_" + date, "true");
}

export function isGameCompleted(date: string): boolean {
  return localStorage.getItem("game_completed_" + date) === "true";
}

export function saveCurrentStreak(streak: number) {
  const existing = localStorage.getItem("game_streak");
  let max = streak;
  if (existing) {
    try {
      const parsed = JSON.parse(existing);
      max = parsed.max || 0;
      if (streak > max) {
        max = streak;
      }
    } catch {
      // ignore
    }
  }
  localStorage.setItem("game_streak", JSON.stringify({ current: streak, max }));
}

export function incrementTotalGames() {
  const existing = localStorage.getItem("game_stats");
  let total = 1;
  let perfect = 0;
  if (existing) {
    try {
      const parsed = JSON.parse(existing);
      total = (parsed.total || 0) + 1;
      perfect = parsed.perfect || 0;
    } catch {
      // ignore
    }
  }
  localStorage.setItem("game_stats", JSON.stringify({ total, perfect }));
}

export function incrementPerfectGames() {
  const existing = localStorage.getItem("game_stats");
  let total = 1;
  let perfect = 1;
  if (existing) {
    try {
      const parsed = JSON.parse(existing);
      total = (parsed.total || 0) + 1;
      perfect = (parsed.perfect || 0) + 1;
    } catch {
      // ignore
    }
  }
  localStorage.setItem("game_stats", JSON.stringify({ total, perfect }));
}

// Add the missing functions referenced in other files
export function clearGameCompletionState() {
  localStorage.removeItem("game_completed_" + getTodayString());
  localStorage.removeItem("last_game_result");
}

export const clearGameCompleted = clearGameCompletionState; // For backward compatibility

export function getCurrentStreak() {
  const streakData = localStorage.getItem("game_streak");
  if (!streakData) return 0;
  
  try {
    const parsed = JSON.parse(streakData);
    return parsed.current || 0;
  } catch {
    return 0;
  }
}

export function getMaxStreak() {
  const streakData = localStorage.getItem("game_streak");
  if (!streakData) return 0;
  
  try {
    const parsed = JSON.parse(streakData);
    return parsed.max || 0;
  } catch {
    return 0;
  }
}

export function getPerfectPercentage() {
  const totalGames = getTotalGames();
  const perfectGames = getPerfectGames();
  
  if (totalGames === 0) return 0;
  return Math.round((perfectGames / totalGames) * 100);
}

export function getPerfectGames() {
  const statsData = localStorage.getItem("game_stats");
  if (!statsData) return 0;
  
  try {
    const parsed = JSON.parse(statsData);
    return parsed.perfect || 0;
  } catch {
    return 0;
  }
}

export function getTotalGames() {
  const statsData = localStorage.getItem("game_stats");
  if (!statsData) return 0;
  
  try {
    const parsed = JSON.parse(statsData);
    return parsed.total || 0;
  } catch {
    return 0;
  }
}

export function getWordHistory() {
  const historyData = localStorage.getItem("word_history");
  if (!historyData) return [];
  
  try {
    return JSON.parse(historyData);
  } catch {
    return [];
  }
}

export function saveWordHistory(words) {
  localStorage.setItem("word_history", JSON.stringify(words));
}

// Add missing functions that are referenced in the error messages
export function isViewingResults(): boolean {
  return localStorage.getItem("viewing_results") === "true";
}

export function setViewingResults(viewing: boolean): void {
  if (viewing) {
    localStorage.setItem("viewing_results", "true");
  } else {
    localStorage.removeItem("viewing_results");
  }
}

export function getGameResultDate(): string | null {
  const result = getLastGameResult();
  return result?.completedDate || null;
}

// Add missing puzzle storage functions
export function getSavedPuzzle(): PuzzleData | null {
  const savedPuzzle = localStorage.getItem("current_puzzle");
  return savedPuzzle ? JSON.parse(savedPuzzle) : null;
}

export function savePuzzle(puzzle: PuzzleData): void {
  localStorage.setItem("current_puzzle", JSON.stringify(puzzle));
}
